import { useEffect, useState } from 'react'
import { Outlet, useOutletContext, useParams } from 'react-router-dom'
import i18n from 'lib/i18'
import { useLocale } from 'providers/LocaleProvider'
import { useGetMasterProductFields, useProduct } from 'hooks/useMasterProducts'
import LayoutWithSecondarySidebar from 'components/Layout/LayoutWithSecondarySidebar'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { MasterProduct } from 'types/api/product'
import {
  ProductAttributeGroup,
  ProductNutrientHeader,
} from 'types/model/product'
import { contentLanguages } from 'lib/languages'
import theme from 'lib/theme'
import { ProductSecondarySidebar } from './ProductSecondarySidebar'

type ProductFields = {
  gtin: string
  name: string
  visibleToConsumers: boolean
  percentageOfAlcohol: number | null
  productType: string
  productName: { [lang: string]: string }
  variantName: { [lang: string]: string }
  description: { [lang: string]: string }
  brand: { [lang: string]: string }
  untranslatedBrand: string
  countryOfOrigin: { [lang: string]: string }
  countryCode: string
  categories:
    | {
        tree: string
        category: { extId: string }
      }[]
    | null
  functionalName: { [lang: string]: string }
  searchKeywords: { [lang: string]: string }
  boostValue: number
  dietTypes: { dietTypeCode: string; dietTypeSubcode?: string }[]
  displayUnit: string
  netContent: number
  netContentUnit: string
  pricedBy: string
  pickedBy: string
  variableTradeItemTypeCode: string
  incrementValue: number
  initialValue: number
  salesConditions: string[]
  nutrientHeaders: ProductNutrientHeader[]
  packagingType: string | null
  packagingMaterial: {
    packagingMaterialTypeCode: string
    isPackagingMaterialRecoverable: boolean
  }[]
  width: number | null
  height: number | null
  depth: number | null
  grossWeight: number | null
  widthUnit: string | null
  depthUnit: string | null
  heightUnit: string | null
  grossWeightUnit: string | null
  storageInformation: { [lang: string]: string }
  preparationUsage: { [lang: string]: string }
  productAttributeGroup: ProductAttributeGroup[]
  equivalentProducts: { gtin: string; referencedTradeItemTypeCode: string }[]
  alternativeProducts: { gtin: string; referencedTradeItemTypeCode: string }[]
}

type ProductContextType = {
  product: ProductFields
  error: unknown
  isLoading: boolean
  masterProduct: MasterProduct
}

export const Product = () => {
  const { masterProductId } = useParams()
  const {
    product: masterProduct,
    isLoading,
    error,
  } = useProduct(masterProductId as string)
  const { product } = useGetMasterProductFields(
    masterProductId as string,
    masterProduct as MasterProduct,
    isLoading,
    error
  )
  const [productName, setProductName] = useState('')
  const { selectedLocale } = useLocale()

  useEffect(() => {
    if (product.productName) {
      const productNameInLang =
        product.productName[i18n.language]?.length > 0
          ? product.productName[i18n.language]
          : product.productName[contentLanguages[0]]
      setProductName(productNameInLang)
    }
  }, [i18n.language, product, selectedLocale])

  if (error || !masterProductId) return <h1>Error</h1>
  if (isLoading) {
    return <LoadingIndicator />
  }
  return (
    <LayoutWithSecondarySidebar
      sx={{
        borderLeft: `1px solid ${theme.palette.neutral.grey7}`,
        height: '100%',
      }}
      sidebar={
        <ProductSecondarySidebar
          gtin={product.gtin || ''}
          productName={productName}
          masterProductId={masterProductId}
        />
      }>
      <Outlet context={{ product, isLoading, error, masterProduct }} />
    </LayoutWithSecondarySidebar>
  )
}

export function useProductInfo() {
  return useOutletContext<ProductContextType>()
}
